import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  MapPin,
  Bed,
  Bath,
  Users,
  Calendar,
  Image as ImageIcon,
  Sparkles,
} from "lucide-react";
import { api } from "../lib/api";

export default function HostProfile() {
  const { hostId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/hosts/${hostId}`);
        setData(r.data);
      } catch (e) {
        setData(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [hostId]);

  if (loading)
    return <div className="max-w-6xl mx-auto px-6 py-20 text-[var(--muted)]">Loading host…</div>;

  if (!data) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center" data-testid="host-not-found">
        <h1 className="font-display text-5xl text-[var(--emerald)]">Host not found.</h1>
        <p className="text-[var(--ink-2)] mt-4">This host may have stepped away from the network.</p>
        <button onClick={() => navigate("/hosts")} className="btn-primary mt-8">
          Back to all hosts
        </button>
      </div>
    );
  }

  const { host, listings } = data;
  const joinedYear = host?.joined ? new Date(host.joined).getFullYear() : null;

  const planTrip = (a) => {
    navigate(
      `/chat?seed=${encodeURIComponent(
        `I'd like to plan a trip to ${a.location} and stay at ${a.name}.`,
      )}`,
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12" data-testid="host-profile">
      <button onClick={() => navigate("/hosts")} className="btn-ghost mb-6">
        <ArrowLeft size={14} strokeWidth={1.25} /> All hosts
      </button>

      {/* Host header */}
      <div className="grid md:grid-cols-3 gap-10 items-start" data-testid="host-header">
        <div className="md:col-span-2">
          <div className="overline">Janté Host</div>
          <h1 className="font-display text-6xl text-[var(--emerald)] mt-3 leading-[1]">
            {host.name}
          </h1>
          <div className="text-sm text-[var(--ink-2)] mt-4 flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5">
              <Sparkles size={12} strokeWidth={1.25} className="text-[var(--gold)]" />
              {listings.length} curated {listings.length === 1 ? "property" : "properties"}
            </span>
            {joinedYear && (
              <>
                <span className="opacity-40">·</span>
                <span>Hosting since {joinedYear}</span>
              </>
            )}
          </div>
        </div>
        <div className="lux-card-dark">
          <div className="overline text-[var(--gold)]">Want this host's stays in your trip?</div>
          <p className="text-sm opacity-90 mt-3">
            Sasha can fold any of these properties into a complete itinerary — flights, drivers, dining, and curated experiences.
          </p>
          <Link to="/chat" className="btn-gold w-full mt-5" data-testid="host-profile-talk-sasha">
            Plan with Sasha
          </Link>
        </div>
      </div>

      {/* Listings */}
      <div className="mt-14">
        <div className="overline">The collection</div>
        <h2 className="font-display text-4xl text-[var(--emerald)] mt-2 mb-8">
          Properties by {host.name}
        </h2>

        {listings.length === 0 ? (
          <div className="lux-card text-center" data-testid="host-no-listings">
            <p className="text-[var(--ink-2)] text-sm">
              {host.name} hasn't published a public stay yet — Sasha can still pair you with their network.
            </p>
            <Link to="/chat" className="btn-primary mt-5">
              Plan with Sasha
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {listings.map((a, i) => (
              <article
                key={a.id}
                className="lux-card flex flex-col cursor-pointer transition-transform hover:-translate-y-0.5"
                data-testid={`host-listing-${i}`}
                onClick={() => planTrip(a)}
              >
                <div className="aspect-[16/10] -m-8 mb-4 overflow-hidden bg-[var(--ivory-2)] relative">
                  {a.images?.[0] ? (
                    <img src={a.images[0]} alt={a.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[var(--muted)]">
                      <ImageIcon size={28} strokeWidth={1.25} />
                    </div>
                  )}
                  <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 bg-[var(--ivory)]/95 text-[10px] uppercase tracking-[0.18em] text-[var(--emerald)]">
                    {a.property_type || "Villa"}
                  </div>
                </div>
                <div className="overline flex items-center gap-1.5">
                  <MapPin size={12} strokeWidth={1.25} /> {a.location}
                </div>
                <h3 className="font-display text-2xl text-[var(--emerald)] mt-1">{a.name}</h3>
                <p className="text-sm text-[var(--ink-2)] mt-2 line-clamp-2">{a.description}</p>
                <div className="text-xs text-[var(--ink-2)] mt-4 grid grid-cols-2 gap-y-1.5">
                  <span className="flex items-center gap-1.5">
                    <Bed size={13} strokeWidth={1.25} className="text-[var(--gold)]" /> {a.bedrooms} bed
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Bath size={13} strokeWidth={1.25} className="text-[var(--gold)]" /> {a.bathrooms || 1} bath
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Users size={13} strokeWidth={1.25} className="text-[var(--gold)]" /> Sleeps {a.max_guests}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Calendar size={13} strokeWidth={1.25} className="text-[var(--gold)]" /> Min {a.min_nights || 2} nts
                  </span>
                </div>
                <div className="mt-5 flex items-baseline justify-between border-t border-[var(--emerald)]/10 pt-4">
                  <div>
                    <div className="font-display text-3xl text-[var(--emerald)] tabular-nums">
                      ${(a.price_per_night || 0).toLocaleString()}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--muted)] mt-0.5">
                      per night
                    </div>
                  </div>
                  <div className="btn-gold !py-2 !px-3 text-[10px]">Plan with Sasha</div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
